import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { BLOG_POSTS } from '../data/blog-posts.js';
import { WhatsAppGlyph } from '../components/Icons.jsx';
import SEO from '../components/SEO.jsx';
import useReveal from '../hooks/useReveal.js';
import { WA_URL } from '../config.js';
import { buildArticleSchema, organizationSchema } from '../data/schemas.js';

function fmtShort(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getFullYear()).slice(2)}`;
}

function PostImage({ slug, blockId, placeholder, aspect = '16/9' }) {
  return (
    <figure className="bp-image-figure" style={{ aspectRatio: aspect }}>
      <image-slot
        id={`blog-${slug}-${blockId}`}
        shape="rounded"
        radius="14"
        placeholder={placeholder || 'גררו תמונה לכאן'}
        style={{ width:'100%', height:'100%' }}
      ></image-slot>
    </figure>
  );
}

function PostVideo({ slug, blockId, placeholder, defaultUrl }) {
  const storageKey = `blog-video-${slug}-${blockId}`;
  const [url, setUrl] = useState(() => { try { return localStorage.getItem(storageKey) || defaultUrl || ''; } catch { return defaultUrl || ''; } });
  const [editing, setEditing] = useState(!url);
  const [draft, setDraft] = useState(url);

  function save(v) { try { localStorage.setItem(storageKey, v); } catch {} setUrl(v); setEditing(false); }
  function ytId(u) { if (!u) return null; const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/); return m ? m[1] : null; }
  const id = ytId(url);

  if (id && !editing) return (
    <figure className="bp-video-figure">
      <div className="bp-video-frame">
        <iframe src={`https://www.youtube.com/embed/${id}`} title="YouTube video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
      </div>
      <button className="bp-video-edit" type="button" onClick={() => { setDraft(url); setEditing(true); }}>החלף וידאו</button>
    </figure>
  );

  return (
    <figure className="bp-video-figure">
      <div className="bp-video-empty">
        <div className="bp-video-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="14" height="12" rx="2"/><path d="M22 8l-6 4 6 4z"/></svg>
        </div>
        <div className="bp-video-empty-title">{placeholder || 'הוסיפו וידאו לפוסט'}</div>
        <div className="bp-video-empty-sub">הדביקו לינק YouTube</div>
        <div className="bp-video-input-row">
          <input type="url" value={draft} placeholder="https://youtube.com/watch?v=..." onChange={e => setDraft(e.target.value)} />
          <button type="button" onClick={() => save(draft)}>שמירה</button>
        </div>
        {url && <button className="bp-video-clear" type="button" onClick={() => { save(''); setDraft(''); setEditing(true); }}>נקה</button>}
      </div>
    </figure>
  );
}

function TableOfContents({ body }) {
  const [open, setOpen] = useState(false);
  const headings = body.filter(b => b.type === 'h2');
  if (headings.length < 2) return null;
  return (
    <details className="bp-toc" open={open} onToggle={e => setOpen(e.currentTarget.open)}>
      <summary>
        <span className="bp-toc-label">תוכן עניינים</span>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bp-toc-chev"><polyline points="6 9 12 15 18 9"/></svg>
      </summary>
      <ol>
        {headings.map((h, i) => (
          <li key={i}>
            <a href={`#h-${i}`} onClick={e => { e.preventDefault(); const el = document.getElementById(`h-${i}`); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 90, behavior: 'smooth' }); }}>
              <span className="bp-toc-n">{String(i+1).padStart(2,'0')}</span>
              {h.text}
            </a>
          </li>
        ))}
      </ol>
    </details>
  );
}

function ShareRow({ title }) {
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const enc = encodeURIComponent;
  return (
    <div className="bp-share">
      <span className="bp-share-label">שיתוף</span>
      <a className="bp-share-btn" href={`https://wa.me/?text=${enc(title + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12.04 2C6.6 2 2.2 6.4 2.2 11.85a9.8 9.8 0 0 0 1.4 5.07L2 22l5.25-1.37a9.85 9.85 0 0 0 4.79 1.23h.01c5.43 0 9.84-4.4 9.84-9.85 0-2.63-1.02-5.1-2.88-6.96A9.78 9.78 0 0 0 12.05 2zm0 1.83c2.13 0 4.13.84 5.65 2.36a8 8 0 0 1 2.34 5.66c0 4.42-3.6 8.02-8.01 8.02-1.42 0-2.81-.38-4.02-1.09l-.29-.17-2.97.78.79-2.89-.19-.3a8 8 0 0 1-1.23-4.28c0-4.43 3.6-8.03 8.01-8.03z"/></svg>
      </a>
      <a className="bp-share-btn" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.25-1.5 1.55-1.5h1.65V4.6a22 22 0 0 0-2.4-.12c-2.4 0-4 1.46-4 4.14V11H7.6v3.1h2.7V22h3.2z"/></svg>
      </a>
      <a className="bp-share-btn" href={`https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="X">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.53 3H20.5l-6.55 7.49L22 21h-6.17l-4.83-6.32L5.4 21H2.43l7-8.01L2 3h6.32l4.37 5.78L17.53 3zm-2.16 16.2h1.64L7.7 4.7H5.94l9.43 14.5z"/></svg>
      </a>
      <button className="bp-share-btn" type="button" onClick={() => navigator.clipboard && navigator.clipboard.writeText(shareUrl)} aria-label="העתק קישור">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1"/><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"/></svg>
      </button>
    </div>
  );
}

function SidebarNewsletter() {
  return (
    <aside className="sb-card sb-newsletter">
      <div className="sb-card-eyebrow">FocusMode · עדכונים</div>
      <h3 className="sb-card-title">לישון רגוע, לעבוד חכם.</h3>
      <p className="sb-card-body">מייל אחד בשבוע על מערכות, AI ותהליכים — מהפרויקטים שבשטח. בלי ספאם, רק מה שעובד.</p>
      <form className="sb-form" onSubmit={e => e.preventDefault()}>
        <input type="email" required placeholder="כתובת מייל" aria-label="email" />
        <button type="submit">הרשמה</button>
      </form>
      <p className="sb-fine">בלחיצה אני אישור לקבלת מסרים. ניתן להסיר בכל עת.</p>
    </aside>
  );
}

function SidebarServices() {
  const items = [
    { slug:'web', label:'בניית אתרים', note:'תדמית, נחיתה, מערכות ווב' },
    { slug:'automations', label:'אוטומציות', note:'לידים, מסמכים, תהליכים חוזרים' },
    { slug:'systems', label:'מערכות ואינטגרציות', note:'חיבורי כלים, פאנלים, CRM' },
  ];
  return (
    <aside className="sb-card">
      <div className="sb-card-eyebrow">השירותים של FocusMode</div>
      <ul className="sb-list">
        {items.map(it => (
          <li key={it.slug}>
            <Link to={`/services/${it.slug}`}>
              <span className="sb-list-title">{it.label}</span>
              <span className="sb-list-note">{it.note}</span>
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function SidebarPopular({ excludeSlug }) {
  const items = BLOG_POSTS.filter(p => p.slug !== excludeSlug).slice(0, 3);
  return (
    <aside className="sb-card">
      <div className="sb-card-eyebrow">הפופולריים ביותר</div>
      <ul className="sb-popular">
        {items.map((p, i) => (
          <li key={p.slug}>
            <Link to={`/blog/${p.slug}`}>
              <span className="sb-pop-num">{String(i+1).padStart(2,'0')}</span>
              <span className="sb-pop-body">
                <span className="sb-pop-title">{p.title}</span>
                <span className="sb-pop-meta">{p.readingTime}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export default function BlogPost() {
  useReveal();
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) return (
    <main style={{ padding:'120px 0', textAlign:'center' }}>
      <SEO title="פוסט לא נמצא" path="/blog" />
      <div className="wrap">
        <h1>פוסט לא נמצא</h1>
        <Link to="/blog" style={{ marginTop:'24px', display:'inline-block' }}>← חזרה לבלוג</Link>
      </div>
    </main>
  );

  let h2idx = -1;
  const blocks = post.body.map(b => {
    if (b.type === 'h2') { h2idx += 1; return { ...b, _hid: h2idx }; }
    return b;
  });

  return (
    <main className="blog-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image || '/og/default.png'}
        schema={[buildArticleSchema(post), organizationSchema].filter(Boolean)}
      />
      <article className="bp-article">
        <div className="wrap bp-wrap">

          <nav className="bp-crumb">
            <Link to="/">דף הבית</Link>
            <span className="sep">›</span>
            <Link to="/blog">בלוג</Link>
            <span className="sep">›</span>
            <span className="bp-crumb-current">{post.title}</span>
          </nav>

          <header className="bp-head reveal">
            <div className="bp-eyebrow">
              <Link to="/blog">{post.eyebrow}</Link>
            </div>
            <h1 className="bp-title">{post.title}</h1>
            <div className="bp-meta-row">
              <div className="bp-author">
                <span className="bp-author-avatar" aria-hidden="true">YR</span>
                <span>
                  <span className="bp-author-name">יואב רון</span>
                  <span className="bp-author-role">FocusMode · יזם, יועץ, מנהל פרויקטים</span>
                </span>
              </div>
              <div className="bp-meta-right">
                <span className="bp-meta-chip">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                  {fmtShort(post.date)}
                </span>
                <span className="bp-meta-chip">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                  {post.readingTime}
                </span>
              </div>
            </div>
          </header>

          <div className="bp-layout">
            <div className="bp-main">
              <div className="reveal">
                <PostImage slug={post.slug} blockId="hero" placeholder={post.heroPlaceholder} aspect="16/9" />
              </div>
              <ShareRow title={post.title} />
              <TableOfContents body={post.body} />

              <div className="bp-body reveal">
                {blocks.map((block, i) => {
                  if (block.type === 'h2') return <h2 key={i} id={`h-${block._hid}`}>{block.text}</h2>;
                  if (block.type === 'p') return <p key={i}>{block.text}</p>;
                  if (block.type === 'ul') return <ul key={i}>{block.items.map((item, j) => <li key={j}>{item}</li>)}</ul>;
                  if (block.type === 'pull') return <blockquote key={i} className="bp-pull">{block.text}</blockquote>;
                  if (block.type === 'image') return <PostImage key={i} slug={post.slug} blockId={block.id} placeholder={block.placeholder} aspect={block.aspect || '16/9'} />;
                  if (block.type === 'video') return <PostVideo key={i} slug={post.slug} blockId={block.id} placeholder={block.placeholder} defaultUrl={block.url} />;
                  return null;
                })}
              </div>

              <div className="bp-endcta reveal">
                <div className="bp-endcta-eyebrow">הישארו מעודכנים</div>
                <h3 className="bp-endcta-title">רוצים פוסט כזה פעם בשבועיים?</h3>
                <p className="bp-endcta-body">נרשמים לניוזלטר ומקבלים את הפוסט הבא ישר למייל — בלי ספאם, רק כתיבה מהשטח.</p>
                <form className="bp-endcta-form" onSubmit={e => e.preventDefault()}>
                  <input type="email" required placeholder="כתובת מייל" />
                  <button type="submit">הרשמה לניוזלטר</button>
                </form>
              </div>

              <div className="bp-talk reveal">
                <div>
                  <div className="bp-talk-eyebrow">יש לך שאלה על הפוסט?</div>
                  <div className="bp-talk-title">כתבו לי 3 שורות בוואטסאפ. אני אוהב לדבר על דברים שכתבתי.</div>
                </div>
                <a className="bp-talk-btn" href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <WhatsAppGlyph size={18} />
                  לוואטסאפ
                </a>
              </div>

              <div className="bp-related reveal">
                <div className="bp-related-eyebrow">פוסטים נוספים</div>
                <div className="bp-related-grid">
                  {BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 2).map(p => (
                    <Link key={p.slug} className="bp-related-card" to={`/blog/${p.slug}`}>
                      <div className="bp-related-media">
                        <image-slot
                          id={`blog-cover-${p.slug}`}
                          shape="rounded"
                          radius="10"
                          placeholder={p.heroPlaceholder || 'תמונה'}
                          style={{ width:'100%', height:'100%' }}
                        ></image-slot>
                      </div>
                      <div className="bp-related-body">
                        <div className="bp-related-tag">{p.eyebrow}</div>
                        <div className="bp-related-title">{p.title}</div>
                        <div className="bp-related-meta">{p.readingTime}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <aside className="bp-side">
              <SidebarNewsletter />
              <SidebarServices />
              <SidebarPopular excludeSlug={slug} />
            </aside>
          </div>
        </div>
      </article>
    </main>
  );
}
